import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Search, 
  Shield, 
  Crown, 
  User, 
  Check, 
  X, 
  AlertTriangle,
  Loader2,
  Sparkles,
  CreditCard,
  Calendar,
  History,
  RefreshCw,
  Filter,
  Users,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// Admin emails that can access this page
const ADMIN_EMAILS = [
  'manuemslaclierxter@gmail.com',
  'manuemsla@gmail.com',
  'felixmanuela.manda@gmail.com'
];

// Filter options
const FILTER_OPTIONS = [
  { value: null, label: 'Tous les utilisateurs', icon: Users },
  { value: 'founding_visionary', label: 'Founding Visionaries', icon: Crown },
  { value: 'standard', label: 'Utilisateurs standards', icon: User },
  { value: 'active_subscription', label: 'Abonnements actifs', icon: Check },
  { value: 'cancelled_subscription', label: 'Abonnements annulés', icon: X },
  { value: 'no_subscription', label: 'Sans abonnement', icon: CreditCard },
  { value: 'monthly', label: 'Abonnement mensuel', icon: Calendar },
  { value: 'annual', label: 'Abonnement annuel', icon: Calendar },
];

const FounderAdmin = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Search state
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  
  // User list state
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(null);
  
  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [showAuditDetails, setShowAuditDetails] = useState(null);
  
  // View mode
  const [viewMode, setViewMode] = useState('search'); // 'search' or 'list'
  
  // Check if current user is admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());
  
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    if (!isAdmin) {
      toast.error('Accès non autorisé');
      navigate('/dashboard');
      return;
    }
    
    // Load audit log and user count on mount
    fetchAuditLog();
    fetchUserCount();
  }, [user, isAdmin, navigate]);
  
  // Fetch just the count for the tab label
  const fetchUserCount = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/admin/users?limit=1`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching user count:', error);
    }
  };
  
  const fetchAuditLog = async () => {
    if (!token) return;
    
    setLoadingAudit(true);
    try {
      const response = await axios.get(`${API}/admin/founding-visionary-audit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLog(response.data.audit_log || []);
    } catch (error) {
      console.error('Error fetching audit log:', error);
    } finally {
      setLoadingAudit(false);
    }
  };
  
  const fetchUserList = async (filter = null) => {
    if (!token) return;
    
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append('filter_type', filter);
      params.append('limit', '50');
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserList(response.data.users || []);
      setTotalUsers(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoadingUsers(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }
    
    setSearching(true);
    setSearchResult(null);
    
    try {
      const response = await axios.get(`${API}/admin/debug-user/${encodeURIComponent(searchEmail)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.error) {
        toast.error('Utilisateur non trouvé');
        setSearchResult(null);
      } else {
        setSearchResult(response.data);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Utilisateur non trouvé');
      } else {
        toast.error('Erreur lors de la recherche');
      }
      setSearchResult(null);
    } finally {
      setSearching(false);
    }
  };
  
  const handleGrantFV = async () => {
    if (!searchResult?.user?.email) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/grant-founding-visionary/${encodeURIComponent(searchResult.user.email)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Statut Founding Visionary accordé à ${searchResult.user.name || searchResult.user.email}`);
      setShowConfirmation(null);
      
      // Refresh data
      handleSearch();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'attribution');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleRevokeFV = async () => {
    if (!searchResult?.user?.email) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/revoke-founding-visionary/${encodeURIComponent(searchResult.user.email)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Statut Founding Visionary révoqué pour ${searchResult.user.name || searchResult.user.email}`);
      setShowConfirmation(null);
      
      // Refresh data
      handleSearch();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la révocation');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setShowFilters(false);
    fetchUserList(filter);
  };
  
  const selectUserFromList = (userEmail) => {
    setSearchEmail(userEmail);
    setViewMode('search');
    setTimeout(() => handleSearch(), 100);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (!isAdmin) {
    return null;
  }
  
  const userData = searchResult?.user;
  const isFoundingVisionary = searchResult?.is_founding_visionary;
  const subscription = searchResult?.subscription;
  
  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <header className="border-b border-[#1C1C1C] bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-[#D4AF37]/20 to-[#A57A03]/20 rounded-lg">
                <Shield className="text-[#D4AF37]" size={24} />
              </div>
              <div>
                <h1 className="font-serif text-xl sm:text-2xl text-white">Founder Admin</h1>
                <p className="text-[#71717A] text-sm">Gestion des Membres & Founding Visionaries</p>
              </div>
            </div>
            
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#D4AF37]/50"
            >
              Retour au Dashboard
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* View Mode Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setViewMode('search')}
            variant={viewMode === 'search' ? 'default' : 'outline'}
            className={viewMode === 'search' 
              ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#B8960C]' 
              : 'border-[#27272A] text-[#A1A1AA]'
            }
          >
            <Search size={16} className="mr-2" />
            Recherche
          </Button>
          <Button
            onClick={() => {
              setViewMode('list');
              if (userList.length === 0) fetchUserList(selectedFilter);
            }}
            variant={viewMode === 'list' ? 'default' : 'outline'}
            className={viewMode === 'list' 
              ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#B8960C]' 
              : 'border-[#27272A] text-[#A1A1AA]'
            }
          >
            <Users size={16} className="mr-2" />
            Liste ({totalUsers})
          </Button>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Panel */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Search Mode */}
            {viewMode === 'search' && (
              <>
                {/* Search Box */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0A0A0A] border border-[#1C1C1C] p-4 sm:p-6"
                >
                  <h2 className="font-serif text-lg sm:text-xl text-white mb-4 sm:mb-6 flex items-center gap-3">
                    <Search className="text-[#D4AF37]" size={20} />
                    Rechercher un Utilisateur
                  </h2>
                  
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Input
                      type="email"
                      value={searchEmail}
                      onChange={(e) => setSearchEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Entrez l'adresse email..."
                      className="flex-1 bg-[#050505] border-[#27272A] text-white placeholder:text-[#52525B]"
                      data-testid="admin-search-email"
                    />
                    <Button
                      onClick={handleSearch}
                      disabled={searching}
                      className="btn-gold px-6"
                      data-testid="admin-search-btn"
                    >
                      {searching ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <>
                          <Search size={18} className="mr-2" />
                          Rechercher
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
                
                {/* Search Results */}
                <AnimatePresence mode="wait">
                  {searchResult && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="bg-[#0A0A0A] border border-[#1C1C1C] p-4 sm:p-6"
                    >
                      {/* User Info Header */}
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6 pb-6 border-b border-[#1C1C1C]">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#D4AF37]/30 to-[#A57A03]/30 rounded-full flex items-center justify-center">
                            <User className="text-[#D4AF37]" size={24} />
                          </div>
                          <div>
                            <h3 className="font-serif text-lg sm:text-xl text-white">
                              {userData?.name || 'Sans nom'}
                            </h3>
                            <p className="text-[#71717A] text-sm">{userData?.email}</p>
                          </div>
                        </div>
                        
                        {/* Founding Visionary Badge */}
                        {isFoundingVisionary ? (
                          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#A57A03]/20 border border-[#D4AF37]/30 rounded-full w-fit">
                            <Sparkles className="text-[#D4AF37]" size={16} />
                            <span className="text-[#D4AF37] text-sm font-medium">Founding Visionary</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 px-4 py-2 bg-[#1C1C1C] border border-[#27272A] rounded-full w-fit">
                            <User className="text-[#71717A]" size={16} />
                            <span className="text-[#71717A] text-sm">Utilisateur Standard</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Status Grid - Extended Info */}
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
                        {/* Founding Visionary Status */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Crown className={isFoundingVisionary ? 'text-[#D4AF37]' : 'text-[#52525B]'} size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Statut FV</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isFoundingVisionary ? (
                              <>
                                <Check className="text-green-500" size={16} />
                                <span className="text-white text-sm font-medium">Actif</span>
                              </>
                            ) : (
                              <>
                                <X className="text-red-400" size={16} />
                                <span className="text-[#71717A] text-sm">Non actif</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* 20% Discount Status */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles className={isFoundingVisionary ? 'text-[#D4AF37]' : 'text-[#52525B]'} size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Réduction 20%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isFoundingVisionary ? (
                              <>
                                <Check className="text-green-500" size={16} />
                                <span className="text-white text-sm font-medium">Active</span>
                              </>
                            ) : (
                              <>
                                <X className="text-red-400" size={16} />
                                <span className="text-[#71717A] text-sm">Non</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Subscription Status */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <CreditCard className="text-[#52525B]" size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Abonnement</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {subscription?.subscription_status === 'active' ? (
                              <>
                                <Check className="text-green-500" size={16} />
                                <span className="text-white text-sm font-medium capitalize">
                                  {subscription?.current_tier === 'experience' ? 'Experience' : 'Essential'}
                                </span>
                              </>
                            ) : subscription?.subscription_status === 'cancelled' ? (
                              <>
                                <X className="text-orange-400" size={16} />
                                <span className="text-orange-400 text-sm">Annulé</span>
                              </>
                            ) : (
                              <>
                                <X className="text-red-400" size={16} />
                                <span className="text-[#71717A] text-sm">Aucun</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Billing Cycle */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="text-[#52525B]" size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Cycle</span>
                          </div>
                          <span className="text-white text-sm">
                            {subscription?.billing_cycle === 'annual' ? 'Annuel' : 
                             subscription?.billing_cycle === 'monthly' ? 'Mensuel' : 'N/A'}
                          </span>
                        </div>
                        
                        {/* Renewal Date */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <RefreshCw className="text-[#52525B]" size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Renouvellement</span>
                          </div>
                          <span className="text-white text-sm">
                            {formatDate(subscription?.renewal_date)}
                          </span>
                        </div>
                        
                        {/* Registration Date */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="text-[#52525B]" size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Inscription</span>
                          </div>
                          <span className="text-white text-sm">
                            {formatDate(userData?.created_at)}
                          </span>
                        </div>
                        
                        {/* Last Login */}
                        <div className="bg-[#050505] border border-[#1C1C1C] p-3 sm:p-4 rounded-lg col-span-2 lg:col-span-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="text-[#52525B]" size={16} />
                            <span className="text-[#A1A1AA] text-xs sm:text-sm">Dernière connexion</span>
                          </div>
                          <span className="text-white text-sm">
                            {formatDateTime(userData?.last_login)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex gap-4 pt-4 border-t border-[#1C1C1C]">
                        {!isFoundingVisionary ? (
                          <Button
                            onClick={() => setShowConfirmation('grant')}
                            className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#A57A03] text-[#050505] hover:opacity-90"
                            data-testid="admin-grant-fv-btn"
                          >
                            <Sparkles size={18} className="mr-2" />
                            Accorder Founding Visionary
                          </Button>
                        ) : (
                          <Button
                            onClick={() => setShowConfirmation('revoke')}
                            variant="outline"
                            className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                            data-testid="admin-revoke-fv-btn"
                          >
                            <X size={18} className="mr-2" />
                            Révoquer Founding Visionary
                          </Button>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
            
            {/* List Mode */}
            {viewMode === 'list' && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0A0A0A] border border-[#1C1C1C] p-4 sm:p-6"
              >
                {/* Filters Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <h2 className="font-serif text-lg sm:text-xl text-white flex items-center gap-3">
                    <Users className="text-[#D4AF37]" size={20} />
                    Liste des Utilisateurs
                    <span className="text-[#71717A] text-base">({totalUsers})</span>
                  </h2>
                  
                  {/* Filter Dropdown */}
                  <div className="relative">
                    <Button
                      onClick={() => setShowFilters(!showFilters)}
                      variant="outline"
                      className="border-[#27272A] text-[#A1A1AA] w-full sm:w-auto"
                    >
                      <Filter size={16} className="mr-2" />
                      {FILTER_OPTIONS.find(f => f.value === selectedFilter)?.label || 'Filtrer'}
                      {showFilters ? <ChevronUp size={16} className="ml-2" /> : <ChevronDown size={16} className="ml-2" />}
                    </Button>
                    
                    {showFilters && (
                      <div className="absolute right-0 mt-2 w-64 bg-[#0A0A0A] border border-[#1C1C1C] rounded-lg shadow-xl z-10">
                        {FILTER_OPTIONS.map((option) => (
                          <button
                            key={option.value || 'all'}
                            onClick={() => handleFilterChange(option.value)}
                            className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-[#1C1C1C] transition-colors ${
                              selectedFilter === option.value ? 'bg-[#1C1C1C] text-[#D4AF37]' : 'text-[#A1A1AA]'
                            }`}
                          >
                            <option.icon size={16} />
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* User List */}
                {loadingUsers ? (
                  <div className="text-center py-12">
                    <Loader2 className="animate-spin text-[#D4AF37] mx-auto mb-4" size={32} />
                    <p className="text-[#71717A]">Chargement...</p>
                  </div>
                ) : userList.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="text-[#52525B] mx-auto mb-4" size={48} />
                    <p className="text-[#71717A]">Aucun utilisateur trouvé</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {userList.map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => selectUserFromList(u.email)}
                        className="w-full p-4 bg-[#050505] border border-[#1C1C1C] rounded-lg hover:border-[#D4AF37]/30 transition-colors text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              u.is_founding_visionary 
                                ? 'bg-[#D4AF37]/20' 
                                : 'bg-[#1C1C1C]'
                            }`}>
                              {u.is_founding_visionary ? (
                                <Crown className="text-[#D4AF37]" size={18} />
                              ) : (
                                <User className="text-[#52525B]" size={18} />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{u.name || 'Sans nom'}</p>
                              <p className="text-[#71717A] text-sm">{u.email}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {/* Subscription Badge */}
                            <span className={`px-2 py-1 rounded text-xs ${
                              u.subscription_status === 'active' 
                                ? 'bg-green-500/20 text-green-400' 
                                : u.subscription_status === 'cancelled'
                                ? 'bg-orange-500/20 text-orange-400'
                                : 'bg-[#1C1C1C] text-[#52525B]'
                            }`}>
                              {u.subscription_status === 'active' ? u.subscription_tier : 
                               u.subscription_status === 'cancelled' ? 'Annulé' : 'Aucun'}
                            </span>
                            
                            {/* FV Badge */}
                            {u.is_founding_visionary && (
                              <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded text-xs">
                                FV
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
            
            {/* Confirmation Modal */}
            <AnimatePresence>
              {showConfirmation && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                  onClick={() => !actionLoading && setShowConfirmation(null)}
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-[#0A0A0A] border border-[#1C1C1C] p-6 sm:p-8 max-w-md w-full rounded-lg"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="text-center mb-6">
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                        showConfirmation === 'grant' 
                          ? 'bg-[#D4AF37]/20' 
                          : 'bg-red-500/20'
                      }`}>
                        {showConfirmation === 'grant' ? (
                          <Sparkles className="text-[#D4AF37]" size={32} />
                        ) : (
                          <AlertTriangle className="text-red-400" size={32} />
                        )}
                      </div>
                      
                      <h3 className="font-serif text-xl text-white mb-2">
                        {showConfirmation === 'grant' 
                          ? 'Confirmer l\'attribution' 
                          : 'Confirmer la révocation'}
                      </h3>
                      
                      <p className="text-[#A1A1AA] text-sm">
                        {showConfirmation === 'grant' ? (
                          <>
                            Êtes-vous sûr(e) de vouloir accorder le statut <span className="text-[#D4AF37]">Founding Visionary</span> à{' '}
                            <span className="text-white">{userData?.name || userData?.email}</span> ?
                          </>
                        ) : (
                          <>
                            Êtes-vous sûr(e) de vouloir révoquer le statut <span className="text-[#D4AF37]">Founding Visionary</span> de{' '}
                            <span className="text-white">{userData?.name || userData?.email}</span> ?
                          </>
                        )}
                      </p>
                      
                      {showConfirmation === 'grant' && (
                        <div className="mt-4 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg text-left">
                          <p className="text-[#D4AF37] text-sm font-medium mb-2">L'utilisateur recevra :</p>
                          <ul className="text-[#A1A1AA] text-xs space-y-1">
                            <li className="flex items-center gap-2">
                              <Check size={12} className="text-[#D4AF37]" />
                              Badge Founding Visionary permanent
                            </li>
                            <li className="flex items-center gap-2">
                              <Check size={12} className="text-[#D4AF37]" />
                              Réduction de 20% à vie sur tous les abonnements
                            </li>
                          </ul>
                        </div>
                      )}
                      
                      {showConfirmation === 'revoke' && (
                        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-left">
                          <p className="text-red-400 text-sm font-medium mb-2">L'utilisateur perdra :</p>
                          <ul className="text-[#A1A1AA] text-xs space-y-1">
                            <li className="flex items-center gap-2">
                              <X size={12} className="text-red-400" />
                              Badge Founding Visionary
                            </li>
                            <li className="flex items-center gap-2">
                              <X size={12} className="text-red-400" />
                              Réduction de 20% à vie
                            </li>
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-4">
                      <Button
                        onClick={() => setShowConfirmation(null)}
                        disabled={actionLoading}
                        variant="outline"
                        className="flex-1 border-[#27272A] text-[#A1A1AA]"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={showConfirmation === 'grant' ? handleGrantFV : handleRevokeFV}
                        disabled={actionLoading}
                        className={`flex-1 ${
                          showConfirmation === 'grant'
                            ? 'bg-gradient-to-r from-[#D4AF37] to-[#A57A03] text-[#050505]'
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        }`}
                        data-testid="admin-confirm-action-btn"
                      >
                        {actionLoading ? (
                          <Loader2 className="animate-spin" size={18} />
                        ) : (
                          'Confirmer'
                        )}
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Audit Log Panel */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#0A0A0A] border border-[#1C1C1C] p-4 sm:p-6 lg:sticky lg:top-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-lg text-white flex items-center gap-3">
                  <History className="text-[#D4AF37]" size={18} />
                  Journal d'Audit
                </h2>
                <Button
                  onClick={fetchAuditLog}
                  variant="ghost"
                  size="sm"
                  className="text-[#71717A] hover:text-white"
                >
                  <RefreshCw size={14} className={loadingAudit ? 'animate-spin' : ''} />
                </Button>
              </div>
              
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {loadingAudit ? (
                  <div className="text-center py-8">
                    <Loader2 className="animate-spin text-[#D4AF37] mx-auto" size={24} />
                  </div>
                ) : auditLog.length === 0 ? (
                  <p className="text-[#52525B] text-sm text-center py-8">
                    Aucune action enregistrée
                  </p>
                ) : (
                  auditLog.slice(0, 30).map((entry, index) => (
                    <div 
                      key={index}
                      className="p-3 bg-[#050505] border border-[#1C1C1C] rounded-lg cursor-pointer hover:border-[#27272A] transition-colors"
                      onClick={() => setShowAuditDetails(showAuditDetails === index ? null : index)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-1.5 rounded flex-shrink-0 ${
                          entry.action === 'granted' 
                            ? 'bg-green-500/20' 
                            : 'bg-red-500/20'
                        }`}>
                          {entry.action === 'granted' ? (
                            <Check size={12} className="text-green-500" />
                          ) : (
                            <X size={12} className="text-red-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">
                            {entry.user_name || entry.user_email}
                          </p>
                          <p className="text-[#52525B] text-xs">
                            {entry.action === 'granted' ? 'Statut accordé' : 'Statut révoqué'}
                          </p>
                          <p className="text-[#3F3F46] text-xs mt-1">
                            {formatDateTime(entry.timestamp)}
                          </p>
                          
                          {/* Expanded Details */}
                          {showAuditDetails === index && (
                            <div className="mt-3 pt-3 border-t border-[#1C1C1C] space-y-2">
                              <p className="text-[#71717A] text-xs">
                                <span className="text-[#52525B]">Admin:</span> {entry.admin_email}
                              </p>
                              <p className="text-[#71717A] text-xs">
                                <span className="text-[#52525B]">Email:</span> {entry.user_email}
                              </p>
                              {entry.previous_status && (
                                <p className="text-[#71717A] text-xs">
                                  <span className="text-[#52525B]">Avant:</span> {
                                    entry.previous_status.is_founding_visionary ? 'FV Actif' : 'Standard'
                                  }
                                </p>
                              )}
                              {entry.new_status && (
                                <p className="text-[#71717A] text-xs">
                                  <span className="text-[#52525B]">Après:</span> {
                                    entry.new_status.is_founding_visionary ? 'FV Actif' : 'Standard'
                                  }
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <ChevronDown 
                          size={14} 
                          className={`text-[#52525B] transition-transform ${showAuditDetails === index ? 'rotate-180' : ''}`} 
                        />
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <p className="text-[#3F3F46] text-xs text-center mt-4">
                Le journal d'audit est permanent et non modifiable.
              </p>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FounderAdmin;
